import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios, { AxiosError } from 'axios';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  ClipboardDocumentIcon,
  FaceSmileIcon,
  HandRaisedIcon,
  MegaphoneIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  SignalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ColumnResizeHandle } from '../../components/ui/ColumnResizeHandle';
import { HoverTooltip } from '../../components/ui/HoverTooltip';
import { InlineOptionPicker } from '../../components/ui/InlineOptionPicker';
import {
  CollaboratorAutocomplete,
  CollaboratorOption,
} from '../../components/ui/CollaboratorAutocomplete';
import { SupportToneBadge } from '../../components/customerSuccess/SupportToneBadge';
import { SupportTagsPicker } from '../../components/customerSuccess/SupportTagsPicker';
import { SupportAiSuggestButton } from '../../components/customerSuccess/SupportAiSuggestButton';
import { SupportAiSuggestModal } from '../../components/customerSuccess/SupportAiSuggestModal';
import { SupportDeskActionButton } from '../../components/customerSuccess/SupportDeskActionButton';
import { SupportDeskCollapsedPane } from '../../components/customerSuccess/SupportDeskCollapsedPane';
import { SupportDeskPaneCollapseButton } from '../../components/customerSuccess/SupportDeskPaneCollapseButton';
import { SupportMessageContent } from '../../components/customerSuccess/SupportMessageContent';
import { RichTextEditor } from '../../components/editor/RichTextEditor';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useSupportDeskLiveUpdates } from '../../hooks/useSupportDeskLiveUpdates';
import {
  SupportDeskQueueFilter,
  SupportDeskQueueItem,
  SupportDeskQueueResult,
  SupportDeskView,
  addSupportDeskComment,
  archiveSupportDeskTicket,
  claimSupportDeskTicket,
  classifySupportDeskTicket,
  getSupportDeskTicket,
  listSupportDeskQueue,
  reopenSupportDeskTicket,
  resendSupportDeskCommentOutbound,
  returnSupportDeskTicketToAssistant,
  sendSupportDeskSurvey,
  suggestSupportDeskMessage,
  SupportDeskClassifyPayload,
} from '../../services/supportDeskApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  ServiceTicket,
  listTicketTeamMembers,
  listTicketTeams,
  ServiceTicketTeam,
  ServiceTicketTeamMember,
} from '../../services/customerSuccessTicketsApi';
import {
  listActiveServiceCatalogItems,
  ServiceCatalogItem,
} from '../../services/serviceCatalogApi';
import { listOpenProblemsByCompany, ServiceProblem } from '../../services/serviceProblemsApi';
import {
  CannedResponse,
  listDeskCannedResponses,
} from '../../services/customerSuccessEngagementApi';
import { isBlankHtml } from '../../utils/supportMessageContent';
import { SupportBroadcastComposer } from '../../components/customerSuccess/SupportBroadcastComposer';
import { SupportArchivePanel } from '../../components/customerSuccess/SupportArchivePanel';
import {
  ticketChannelTone,
  ticketPriorityTone,
  ticketSlaTone,
  ticketStatusTone,
} from '../../utils/supportDeskBadges';
import { isTypingTarget } from '../../utils/isTypingTarget';
import { useResizablePanelWidth } from '../../hooks/useResizablePanelWidth';
import { useElementMinWidth } from '../../hooks/useElementMinWidth';
import {
  DESK_CLASSIFICATION_PANE_SIZE,
  DESK_COLLAPSED_PANE_SIZE,
  DESK_QUEUES_PANE_SIZE,
  useSupportDeskPaneCollapse,
} from '../../hooks/useSupportDeskPaneCollapse';
import {
  applySupportDeskFilterPreferences,
  hasDeskFilterParamsInUrl,
  loadSupportDeskFilterPreferences,
  saveSupportDeskFilterPreferences,
} from '../../utils/supportDeskFilterPreferences';

const KEYBOARD_HINTS = [
  { keys: 'J / K', label: 'próximo / anterior' },
  { keys: 'Enter', label: 'responder' },
  { keys: 'A', label: 'assumir' },
  { keys: 'Esc', label: 'fechar / limpar' },
] as const;
const DESK_PAGE_SIZE = 50;
const DESK_QUEUE_COLUMN_WIDTH_KEY = 'customer-success.desk.queue-column-width';
const DESK_QUEUE_COLUMN_DEFAULT_WIDTH = 352;
const DESK_QUEUE_COLUMN_MIN_WIDTH = 260;
const DESK_QUEUE_COLUMN_MAX_WIDTH = 640;

const VIEW_OPTIONS: { id: SupportDeskView; label: string; hint: string }[] = [
  {
    id: 'needs_attention',
    label: 'Precisa atenção',
    hint: 'Fila operacional — novos, sem dono, SLA e sem 1ª resposta',
  },
  { id: 'mine', label: 'Comigo', hint: 'Chamados atribuídos a você' },
  { id: 'unassigned', label: 'Sem responsável', hint: 'Disponíveis para assumir' },
  { id: 'grouped', label: 'Agrupados', hint: 'Vinculados a um problema' },
  { id: 'archived', label: 'Arquivados', hint: 'Resolvidos ou fechados' },
];

const PRIORITY_LABELS: Record<string, string> = {
  Low: 'Baixa',
  Medium: 'Média',
  High: 'Alta',
  Critical: 'Crítica',
};

const SLA_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'Breached', label: 'SLA estourado' },
  { value: 'Warning', label: 'SLA em alerta' },
  { value: 'Ok', label: 'SLA ok' },
  { value: 'None', label: 'Sem SLA' },
];

const STATUS_LABELS: Record<string, string> = {
  New: 'Novo',
  Triaged: 'Triado',
  InProgress: 'Em andamento',
  PendingCustomer: 'Aguardando cliente',
  Resolved: 'Resolvido',
  Closed: 'Fechado',
  Reopened: 'Reaberto',
};

const CHANNEL_LABELS: Record<string, string> = {
  Internal: 'Interno',
  Email: 'E-mail',
  Chat: 'Chat',
  Api: 'API',
  Portal: 'Portal',
  Widget: 'Widget',
};

function formatChannelOrigin(
  sourceChannel: string,
  sourceChannelName?: string | null,
): string {
  const typeLabel = CHANNEL_LABELS[sourceChannel] ?? sourceChannel;
  const name = sourceChannelName?.trim();
  return name ? `${typeLabel} · ${name}` : typeLabel;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.round(hours / 24);
  return `${days} d`;
}

function countForView(
  counts: SupportDeskQueueResult['counts'] | undefined,
  view: SupportDeskView,
): number {
  if (!counts) return 0;
  switch (view) {
    case 'mine':
      return counts.mine;
    case 'unassigned':
      return counts.unassigned;
    case 'grouped':
      return counts.grouped;
    case 'archived':
      return counts.archived;
    default:
      return counts.needsAttention;
  }
}

export function SupportDeskPage() {
  const { canWriteModule, canReadModule } = usePermissions();
  const canWrite = canWriteModule('customer-success.desk');
  const canRead = canReadModule('customer-success.desk');
  const [searchParams, setSearchParams] = useSearchParams();

  const view = (searchParams.get('view') as SupportDeskView) || 'needs_attention';
  const selectedId = searchParams.get('selected') ?? undefined;
  const search = searchParams.get('q') ?? '';
  const filaParam = searchParams.get('fila');
  const queueFilter: SupportDeskQueueFilter =
    filaParam === 'all' || VIEW_OPTIONS.some((option) => option.id === filaParam)
      ? (filaParam as SupportDeskQueueFilter)
      : view;
  const priority = searchParams.get('priority') ?? '';
  const teamId = searchParams.get('teamId') ?? '';
  const slaState = searchParams.get('sla') ?? '';
  const tag = searchParams.get('tag') ?? '';
  const catalogItemId = searchParams.get('catalogItemId') ?? '';
  /** Com "Todas", só amplia o escopo quando há texto na busca; senão mantém a fila da esquerda. */
  const effectiveQueueView: SupportDeskQueueFilter =
    queueFilter === 'all' && search.trim() ? 'all' : queueFilter === 'all' ? view : queueFilter;

  const [queue, setQueue] = useState<SupportDeskQueueResult | null>(null);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [loadingMoreQueue, setLoadingMoreQueue] = useState(false);
  const [searchDraft, setSearchDraft] = useState(search);
  const [detail, setDetail] = useState<ServiceTicket | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const detailAbortRef = useRef<AbortController | null>(null);
  const detailRequestIdRef = useRef(0);
  const [replyBody, setReplyBody] = useState('');
  const [replyHtmlMode, setReplyHtmlMode] = useState(false);
  const [replyInternal, setReplyInternal] = useState(false);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [resendingCommentId, setResendingCommentId] = useState<string | null>(null);
  const resendingCommentIdRef = useRef<string | null>(null);
  const [suggestingReply, setSuggestingReply] = useState(false);
  const [aiSuggestOpen, setAiSuggestOpen] = useState(false);
  const [aiSuggestText, setAiSuggestText] = useState('');
  const [aiSuggestError, setAiSuggestError] = useState<string | null>(null);
  const aiSuggestGenerationRef = useRef(0);
  const [classifying, setClassifying] = useState(false);
  const [showGuidance, setShowGuidance] = useState(() => {
    const prefs = loadSupportDeskFilterPreferences();
    return prefs?.showGuidance ?? true;
  });
  const [filterPrefsReady, setFilterPrefsReady] = useState(false);
  const filterPrefsHydratedRef = useRef(false);
  const queueScrollRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreQueueRef = useRef(false);
  const [teamMembers, setTeamMembers] = useState<ServiceTicketTeamMember[]>([]);
  const [teams, setTeams] = useState<ServiceTicketTeam[]>([]);
  const [catalogItems, setCatalogItems] = useState<ServiceCatalogItem[]>([]);
  const [companyProblems, setCompanyProblems] = useState<ServiceProblem[]>([]);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [archivePanel, setArchivePanel] = useState<'archive' | 'reopen' | null>(null);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [sendingSurvey, setSendingSurvey] = useState<'Csat' | 'Nps' | null>(null);
  const [detailHeaderRef, showActionLabels] = useElementMinWidth(360);
  const replyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const queueListRef = useRef<HTMLUListElement | null>(null);
  const {
    width: queueColumnWidth,
    isResizing: isResizingQueueColumn,
    beginResize: beginQueueColumnResize,
  } = useResizablePanelWidth(
    DESK_QUEUE_COLUMN_WIDTH_KEY,
    DESK_QUEUE_COLUMN_DEFAULT_WIDTH,
    DESK_QUEUE_COLUMN_MIN_WIDTH,
    DESK_QUEUE_COLUMN_MAX_WIDTH,
  );
  const [isLargeLayout, setIsLargeLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const [isXlLayout, setIsXlLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1280px)').matches,
  );
  const {
    queuesCollapsed,
    ticketsCollapsed,
    classificationCollapsed,
    toggleQueues,
    toggleTickets,
    toggleClassification,
  } = useSupportDeskPaneCollapse();

  const activeView = VIEW_OPTIONS.find((item) => item.id === view) ?? VIEW_OPTIONS[0];

  const assigneeOptions: CollaboratorOption[] = useMemo(
    () =>
      teamMembers.map((member) => ({
        userId: member.id,
        name: member.name,
        email: member.email,
      })),
    [teamMembers],
  );

  const statusOptions = useMemo(
    () =>
      Object.entries(STATUS_LABELS).map(([id, label]) => ({
        id,
        label,
      })),
    [],
  );

  const priorityOptions = useMemo(
    () =>
      Object.entries(PRIORITY_LABELS).map(([id, label]) => ({
        id,
        label,
      })),
    [],
  );

  const catalogOptions = useMemo(
    () =>
      catalogItems.map((item) => ({
        id: item.id,
        label: item.name,
        description: item.description || undefined,
      })),
    [catalogItems],
  );

  const teamOptions = useMemo(
    () => teams.map((team) => ({ id: team.id, label: team.name })),
    [teams],
  );

  const problemOptions = useMemo(
    () =>
      companyProblems.map((problem) => ({
        id: problem.id,
        label: problem.problemCode,
        description: problem.title,
      })),
    [companyProblems],
  );

  useEffect(() => {
    const largeMedia = window.matchMedia('(min-width: 1024px)');
    const xlMedia = window.matchMedia('(min-width: 1280px)');
    const updateLayout = (): void => {
      setIsLargeLayout(largeMedia.matches);
      setIsXlLayout(xlMedia.matches);
    };
    updateLayout();
    largeMedia.addEventListener('change', updateLayout);
    xlMedia.addEventListener('change', updateLayout);
    return () => {
      largeMedia.removeEventListener('change', updateLayout);
      xlMedia.removeEventListener('change', updateLayout);
    };
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const [members, teamList, catalog] = await Promise.all([
          listTicketTeamMembers(),
          listTicketTeams(),
          listActiveServiceCatalogItems(),
        ]);
        setTeamMembers(members);
        setTeams(teamList);
        setCatalogItems(catalog);
      } catch {
        /* lookups opcionais */
      }
    })();
  }, []);

  useEffect(() => {
    if (!detail?.companyId) {
      setCompanyProblems([]);
      return;
    }
    void listOpenProblemsByCompany(detail.companyId)
      .then(setCompanyProblems)
      .catch(() => setCompanyProblems([]));
  }, [detail?.companyId]);

  const activeFiltersCount = [
    priority,
    teamId,
    slaState,
    tag,
    catalogItemId,
    search,
    queueFilter === 'all' ? 'all' : '',
  ].filter(Boolean).length;

  const loadQueue = useCallback(async (options?: {
    cursor?: string;
    append?: boolean;
    silent?: boolean;
  }): Promise<void> => {
    const append = options?.append ?? false;
    const silent = options?.silent ?? false;

    if (!append) {
      if (!silent) {
        setLoadingQueue(true);
      }
    } else {
      if (loadingMoreQueueRef.current) return;
      loadingMoreQueueRef.current = true;
      setLoadingMoreQueue(true);
    }

    try {
      const result = await listSupportDeskQueue({
        view: effectiveQueueView,
        search: search.trim() || undefined,
        priority: priority || undefined,
        teamId: teamId || undefined,
        slaState: slaState || undefined,
        tag: tag || undefined,
        catalogItemId: catalogItemId || undefined,
        cursor: options?.cursor,
        pageSize: DESK_PAGE_SIZE,
      });
      setQueue((current) => ({
        ...result,
        items: append && current ? [...current.items, ...result.items] : result.items,
      }));
    } catch {
      if (!silent) {
        toast.error('Falha ao carregar a fila de atendimento.');
      }
      if (!append && !silent) {
        setQueue(null);
      }
    } finally {
      if (!append) {
        if (!silent) {
          setLoadingQueue(false);
        }
      } else {
        loadingMoreQueueRef.current = false;
        setLoadingMoreQueue(false);
      }
    }
  }, [effectiveQueueView, search, priority, teamId, slaState, tag, catalogItemId]);

  const loadDetail = useCallback(async (ticketId: string, options?: { silent?: boolean }): Promise<void> => {
    const silent = options?.silent ?? false;
    let requestId: number;
    let signal: AbortSignal | undefined;

    if (!silent) {
      detailAbortRef.current?.abort();
      const controller = new AbortController();
      detailAbortRef.current = controller;
      signal = controller.signal;
      requestId = ++detailRequestIdRef.current;
      setLoadingDetail(true);
      setDetailError(null);
    } else {
      requestId = detailRequestIdRef.current;
    }

    try {
      const [ticket, canned] = await Promise.all([
        getSupportDeskTicket(ticketId, { signal }),
        listDeskCannedResponses(ticketId, { signal }).catch(() => [] as CannedResponse[]),
      ]);
      if (requestId !== detailRequestIdRef.current) {
        return;
      }
      setDetail(ticket);
      setCannedResponses(canned);
      setDetailError(null);
    } catch (error) {
      if (axios.isCancel(error) || (axios.isAxiosError(error) && error.code === 'ERR_CANCELED')) {
        return;
      }
      if (requestId !== detailRequestIdRef.current) {
        return;
      }
      if (!silent) {
        const message = getRequestErrorMessage(error, 'Falha ao abrir o chamado.');
        setDetailError(message);
        setDetail(null);
        setCannedResponses([]);
        toast.error(message);
      }
    } finally {
      if (!silent && requestId === detailRequestIdRef.current) {
        setLoadingDetail(false);
        if (detailAbortRef.current?.signal === signal) {
          detailAbortRef.current = null;
        }
      }
    }
  }, []);

  const cancelDetailLoad = useCallback((): void => {
    detailRequestIdRef.current += 1;
    detailAbortRef.current?.abort();
    detailAbortRef.current = null;
    setLoadingDetail(false);
    setDetail(null);
    setCannedResponses([]);
    setDetailError('Carregamento cancelado.');
  }, []);

  const refreshLive = useCallback((): void => {
    void loadQueue({ silent: true });
    if (selectedId) {
      void loadDetail(selectedId, { silent: true });
    }
  }, [loadQueue, loadDetail, selectedId]);

  useSupportDeskLiveUpdates({
    enabled: canRead,
    onRefresh: refreshLive,
  });

  useEffect(() => {
    if (filterPrefsHydratedRef.current) return;
    filterPrefsHydratedRef.current = true;

    if (!hasDeskFilterParamsInUrl(searchParams)) {
      const prefs = loadSupportDeskFilterPreferences();
      if (prefs) {
        const next = applySupportDeskFilterPreferences(searchParams, prefs);
        if (next.toString() !== searchParams.toString()) {
          setSearchParams(next, { replace: true });
        }
      }
    }

    setFilterPrefsReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filterPrefsReady) return;
    saveSupportDeskFilterPreferences({
      view,
      priority: priority || undefined,
      teamId: teamId || undefined,
      sla: slaState || undefined,
      tag: tag || undefined,
      catalogItemId: catalogItemId || undefined,
      showGuidance,
    });
  }, [filterPrefsReady, view, priority, teamId, slaState, tag, catalogItemId, showGuidance]);

  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchDraft.trim() === search.trim()) return;
      patchFilters({ q: searchDraft.trim() || null });
    }, 280);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    const container = queueScrollRef.current;
    if (!container) return;

    function handleScroll(): void {
      const scrollContainer = queueScrollRef.current;
      if (!scrollContainer || loadingQueue || loadingMoreQueueRef.current || !queue) return;
      if (!queue.nextCursor) return;
      if (scrollContainer.scrollTop + scrollContainer.clientHeight < scrollContainer.scrollHeight - 160) return;
      void loadQueue({ cursor: queue.nextCursor, append: true });
    }

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loadQueue, loadingQueue, queue]);

  useEffect(() => {
    if (!selectedId) {
      detailRequestIdRef.current += 1;
      detailAbortRef.current?.abort();
      detailAbortRef.current = null;
      setDetail(null);
      setDetailError(null);
      setLoadingDetail(false);
      setBroadcastOpen(false);
      setArchivePanel(null);
      return;
    }
    setBroadcastOpen(false);
    setArchivePanel(null);
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    return () => {
      detailAbortRef.current?.abort();
    };
  }, []);

  const selectedQueueItem = useMemo(
    () => queue?.items.find((item) => item.id === selectedId) ?? null,
    [queue, selectedId],
  );

  function patchFilters(patch: Record<string, string | null>): void {
    const params = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value) params.delete(key);
      else params.set(key, value);
    });
    setSearchParams(params);
  }

  function clearFilters(): void {
    const params = new URLSearchParams(searchParams);
    ['q', 'priority', 'teamId', 'sla', 'tag', 'catalogItemId', 'fila'].forEach((key) =>
      params.delete(key),
    );
    setSearchParams(params);
    setSearchDraft('');
  }

  function setView(next: SupportDeskView): void {
    const params = new URLSearchParams(searchParams);
    params.set('view', next);
    // Menu da esquerda redefine o combo Fila para a mesma fila.
    params.delete('fila');
    params.delete('selected');
    setSearchParams(params);
  }

  function setQueueFilter(next: SupportDeskQueueFilter): void {
    if (next === 'all') {
      // Mantém a fila da esquerda; só amplia o escopo da busca.
      patchFilters({ fila: 'all' });
      return;
    }

    const params = new URLSearchParams(searchParams);
    params.set('view', next);
    params.delete('fila');
    params.delete('selected');
    setSearchParams(params);
  }

  const selectTicketById = useCallback(
    (ticketId: string): void => {
      const params = new URLSearchParams(searchParams);
      params.set('view', view);
      params.set('selected', ticketId);
      setSearchParams(params);
      window.requestAnimationFrame(() => {
        const node = queueListRef.current?.querySelector<HTMLElement>(
          `[data-desk-ticket-id="${ticketId}"]`,
        );
        node?.scrollIntoView({ block: 'nearest' });
      });
    },
    [searchParams, setSearchParams, view],
  );

  function selectTicket(item: SupportDeskQueueItem): void {
    selectTicketById(item.id);
  }

  const clearSelection = useCallback((): void => {
    const params = new URLSearchParams(searchParams);
    params.delete('selected');
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const linkedProblem = useMemo(
    () => companyProblems.find((problem) => problem.id === detail?.problemId) ?? null,
    [companyProblems, detail?.problemId],
  );

  async function handleClassify(payload: SupportDeskClassifyPayload): Promise<void> {
    if (!selectedId || !canWrite) return;
    setClassifying(true);
    try {
      const updated = await classifySupportDeskTicket(selectedId, payload);
      setDetail(updated);
      await loadQueue({ silent: true });
    } catch {
      toast.error('Não foi possível atualizar a classificação.');
    } finally {
      setClassifying(false);
    }
  }

  function openBroadcast(): void {
    if (!detail?.problemId || !canWrite) return;
    setArchivePanel(null);
    setBroadcastOpen(true);
  }

  function openArchivePanel(mode: 'archive' | 'reopen'): void {
    if (!canWrite) return;
    setBroadcastOpen(false);
    setArchivePanel(mode);
  }

  const handleClaim = useCallback(async (): Promise<void> => {
    if (!selectedId || !canWrite) return;
    try {
      await claimSupportDeskTicket(selectedId);
      toast.success('Chamado assumido. Assistente pausado.');
      await Promise.all([
        loadQueue({ silent: true }),
        loadDetail(selectedId, { silent: true }),
      ]);
    } catch {
      toast.error('Não foi possível assumir o chamado.');
    }
  }, [selectedId, canWrite, loadQueue, loadDetail]);

  const handleReturnToAssistant = useCallback(async (): Promise<void> => {
    if (!selectedId || !canWrite) return;
    try {
      await returnSupportDeskTicketToAssistant(selectedId);
      toast.success('Conversa devolvida ao assistente.');
      await Promise.all([
        loadQueue({ silent: true }),
        loadDetail(selectedId, { silent: true }),
      ]);
    } catch {
      toast.error('Não foi possível devolver ao assistente.');
    }
  }, [selectedId, canWrite, loadQueue, loadDetail]);

  async function handleArchiveSubmit(reason: string): Promise<void> {
    if (!selectedId || !canWrite) return;
    setArchiveSubmitting(true);
    try {
      await archiveSupportDeskTicket(selectedId, reason);
      toast.success('Chamado arquivado.');
      setArchivePanel(null);
      const params = new URLSearchParams(searchParams);
      params.delete('selected');
      setSearchParams(params);
      await loadQueue({ silent: true });
    } catch {
      toast.error('Não foi possível arquivar o chamado.');
    } finally {
      setArchiveSubmitting(false);
    }
  }

  async function handleSendSurvey(surveyType: 'Csat' | 'Nps'): Promise<void> {
    if (!selectedId || !canWrite || sendingSurvey) return;
    setSendingSurvey(surveyType);
    try {
      const result = await sendSupportDeskSurvey(selectedId, surveyType);
      toast.success(result.message || `Pesquisa ${surveyType.toUpperCase()} enfileirada.`);
    } catch (error) {
      toast.info(
        getRequestErrorMessage(
          error,
          'Não foi possível enfileirar a pesquisa. Verifique o e-mail do contato e a configuração do canal.',
        ),
      );
    } finally {
      setSendingSurvey(null);
    }
  }

  async function handleReopenSubmit(reason: string): Promise<void> {
    if (!selectedId || !canWrite) return;
    setArchiveSubmitting(true);
    try {
      await reopenSupportDeskTicket(selectedId, reason || undefined);
      toast.success('Chamado reaberto.');
      setArchivePanel(null);
      const params = new URLSearchParams(searchParams);
      params.set('view', 'needs_attention');
      params.set('selected', selectedId);
      setSearchParams(params);
      await Promise.all([
        loadQueue({ silent: true }),
        loadDetail(selectedId, { silent: true }),
      ]);
    } catch {
      toast.error('Não foi possível reabrir o chamado.');
    } finally {
      setArchiveSubmitting(false);
    }
  }

  async function handleReply(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selectedId || !canWrite || sendingRef.current) return;

    const htmlMode = replyHtmlMode && !replyInternal;
    const bodyToSend = htmlMode
      ? (isBlankHtml(replyBody) ? '' : replyBody.trim())
      : replyBody.trim();
    if (!bodyToSend && replyFiles.length === 0) return;

    sendingRef.current = true;
    setSending(true);
    try {
      await addSupportDeskComment(selectedId, {
        body: bodyToSend,
        isInternal: replyInternal,
        isHtml: htmlMode,
        files: replyFiles,
      });
      toast.success(replyInternal ? 'Nota interna registrada.' : 'Resposta enviada.');
      setReplyBody('');
      setReplyHtmlMode(false);
      setReplyInternal(false);
      setReplyFiles([]);
      await Promise.all([
        loadQueue({ silent: true }),
        loadDetail(selectedId, { silent: true }),
      ]);
    } catch (err) {
      const detailMsg = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast.error(
        detailMsg
          ?? (replyInternal
            ? 'Falha ao registrar a nota interna.'
            : 'Falha ao enviar a resposta pelo e-mail/canal. A mensagem pode ter ficado no histórico — confira SMTP do canal e tente de novo.'),
      );
      if (!replyInternal && selectedId) {
        await loadDetail(selectedId, { silent: true });
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  async function handleResendComment(commentId: string): Promise<void> {
    if (!selectedId || !canWrite || resendingCommentIdRef.current) return;
    resendingCommentIdRef.current = commentId;
    setResendingCommentId(commentId);
    try {
      await resendSupportDeskCommentOutbound(selectedId, commentId);
      toast.success('Resposta reenviada pelo canal.');
      await Promise.all([
        loadQueue({ silent: true }),
        loadDetail(selectedId, { silent: true }),
      ]);
    } catch (err) {
      const detailMsg = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast.error(detailMsg ?? 'Falha ao reenviar a resposta pelo canal.');
    } finally {
      resendingCommentIdRef.current = null;
      setResendingCommentId(null);
    }
  }

  const closeAiSuggest = useCallback((): void => {
    aiSuggestGenerationRef.current += 1;
    setAiSuggestOpen(false);
    setAiSuggestText('');
    setAiSuggestError(null);
    setSuggestingReply(false);
  }, []);

  function useAiSuggestion(): void {
    const next = aiSuggestText.trim();
    if (!next) return;
    setReplyBody(next);
    closeAiSuggest();
    toast.success(
      replyInternal
        ? 'Sugestão aplicada na nota interna. Revise antes de registrar.'
        : 'Sugestão aplicada na resposta. Revise antes de enviar.',
    );
  }

  async function handleSuggestReply(): Promise<void> {
    if (!selectedId || !canWrite || suggestingReply || sending) return;
    const generation = aiSuggestGenerationRef.current + 1;
    aiSuggestGenerationRef.current = generation;
    setAiSuggestOpen(true);
    setAiSuggestText('');
    setAiSuggestError(null);
    setSuggestingReply(true);
    try {
      const result = await suggestSupportDeskMessage(
        selectedId,
        replyInternal ? 'InternalNote' : 'Reply',
        replyBody,
      );
      if (generation !== aiSuggestGenerationRef.current) return;
      setAiSuggestText(result.text);
    } catch (err) {
      if (generation !== aiSuggestGenerationRef.current) return;
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      const message = detail ?? 'Não foi possível sugerir o texto com IA.';
      setAiSuggestError(message);
      toast.error(message);
    } finally {
      if (generation === aiSuggestGenerationRef.current) {
        setSuggestingReply(false);
      }
    }
  }

  const canShowReplyComposer =
    Boolean(canWrite && detail && selectedQueueItem && !selectedQueueItem.isArchived)
    && !archivePanel
    && !broadcastOpen;

  const canClaimSelected =
    Boolean(canWrite && detail && detail.assignedToUserId == null && selectedQueueItem && !selectedQueueItem.isArchived);

  useEffect(() => {
    function navigateQueue(direction: 1 | -1): void {
      const items = queue?.items ?? [];
      if (items.length === 0) return;

      if (!selectedId) {
        selectTicketById(direction > 0 ? items[0].id : items[items.length - 1].id);
        return;
      }

      const index = items.findIndex((item) => item.id === selectedId);
      if (index < 0) {
        selectTicketById(direction > 0 ? items[0].id : items[items.length - 1].id);
        return;
      }

      const nextIndex = Math.min(items.length - 1, Math.max(0, index + direction));
      if (items[nextIndex]?.id && items[nextIndex].id !== selectedId) {
        selectTicketById(items[nextIndex].id);
      }
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        if (aiSuggestOpen) {
          closeAiSuggest();
          return;
        }
        if (archivePanel) {
          setArchivePanel(null);
          return;
        }
        if (broadcastOpen) {
          setBroadcastOpen(false);
          return;
        }
        if (isTypingTarget(event.target)) {
          (event.target as HTMLElement).blur();
          return;
        }
        if (selectedId) {
          clearSelection();
        }
        return;
      }

      if (isTypingTarget(event.target)) return;

      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;

      if (key === 'j' || key === 'ArrowDown') {
        event.preventDefault();
        navigateQueue(1);
        return;
      }

      if (key === 'k' || key === 'ArrowUp') {
        event.preventDefault();
        navigateQueue(-1);
        return;
      }

      if (key === 'Enter') {
        if (!canShowReplyComposer) return;
        event.preventDefault();
        replyTextareaRef.current?.focus();
        return;
      }

      if (key === 'a') {
        if (!canClaimSelected) return;
        event.preventDefault();
        void handleClaim();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    aiSuggestOpen,
    archivePanel,
    broadcastOpen,
    canClaimSelected,
    canShowReplyComposer,
    clearSelection,
    closeAiSuggest,
    handleClaim,
    queue?.items,
    selectedId,
    selectTicketById,
  ]);

  const deskGridTemplateColumns = useMemo(() => {
    if (!isLargeLayout) return undefined;
    const queues = queuesCollapsed ? DESK_COLLAPSED_PANE_SIZE : DESK_QUEUES_PANE_SIZE;
    const tickets = ticketsCollapsed ? DESK_COLLAPSED_PANE_SIZE : `${queueColumnWidth}px`;
    const conversation = 'minmax(0, 1fr)';
    if (!isXlLayout) {
      return `${queues} ${tickets} ${conversation}`;
    }
    const classification = classificationCollapsed
      ? DESK_COLLAPSED_PANE_SIZE
      : DESK_CLASSIFICATION_PANE_SIZE;
    return `${queues} ${tickets} ${conversation} ${classification}`;
  }, [
    classificationCollapsed,
    isLargeLayout,
    isXlLayout,
    queueColumnWidth,
    queuesCollapsed,
    ticketsCollapsed,
  ]);

  return (
    <div className="-m-4 flex h-[calc(100dvh-3.5rem)] min-h-[32rem] flex-col overflow-hidden bg-secondary-50 md:-m-6 lg:-m-8">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary-200 bg-white px-4 py-3">
        <div>
          <h1 className="text-lg font-semibold text-secondary-900">Atendimento</h1>
          <p className="text-sm text-secondary-500">
            Mesa única para N1–N3, gestão e diretoria — filas, contexto e ação no mesmo lugar.
          </p>
          {showGuidance ? (
            <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-secondary-500">
              <span className="font-medium text-secondary-600">Atalhos:</span>
              {KEYBOARD_HINTS.map((hint) => (
                <span key={hint.keys} className="inline-flex items-center gap-1">
                  <kbd className="rounded border border-secondary-200 bg-secondary-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-secondary-700">
                    {hint.keys}
                  </kbd>
                  <span>{hint.label}</span>
                </span>
              ))}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="erp-btn-secondary text-sm"
            onClick={() => setShowGuidance((value) => !value)}
          >
            {showGuidance ? 'Ocultar dicas' : 'Mostrar dicas'}
          </button>
          <Link to="/customer-success/tickets" className="erp-btn-secondary text-sm">
            Cadastro de chamados
          </Link>
          <Link to="/customer-success/problems" className="erp-btn-secondary text-sm">
            Problemas
          </Link>
        </div>
      </header>

      <div
        className={`grid min-h-0 flex-1 ${isLargeLayout ? '' : 'grid-cols-1'} ${isResizingQueueColumn ? 'select-none' : ''}`}
        style={
          isLargeLayout && deskGridTemplateColumns
            ? { gridTemplateColumns: deskGridTemplateColumns }
            : undefined
        }
      >
        <aside className="hidden min-h-0 flex-col border-r border-secondary-200 bg-white lg:flex">
          {queuesCollapsed ? (
            <SupportDeskCollapsedPane
              label="Filas"
              expandLabel="Expandir filas"
              side="start"
              onExpand={toggleQueues}
            />
          ) : (
            <>
          <div className="flex items-center justify-between gap-2 border-b border-secondary-100 px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
              Filas
            </span>
            <SupportDeskPaneCollapseButton
              collapsed={false}
              side="start"
              collapseLabel="Recolher filas"
              expandLabel="Expandir filas"
              onToggle={toggleQueues}
            />
          </div>
          <nav className="flex-1 space-y-1 overflow-y-auto p-2">
            {VIEW_OPTIONS.map((option) => {
              const active = option.id === view;
              const count = countForView(queue?.counts, option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setView(option.id)}
                  className={`flex w-full items-start justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${
                    active
                      ? 'bg-primary-50 text-primary-900'
                      : 'text-secondary-700 hover:bg-secondary-50'
                  }`}
                >
                  <span>
                    <span className="block font-medium">{option.label}</span>
                    {showGuidance ? (
                      <span className="mt-0.5 block text-xs text-secondary-500">{option.hint}</span>
                    ) : null}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      active ? 'bg-primary-100 text-primary-800' : 'bg-secondary-100 text-secondary-600'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </nav>
            </>
          )}
        </aside>

        <section className="relative flex min-h-0 flex-col border-r border-secondary-200 bg-white">
          {isLargeLayout && ticketsCollapsed ? (
            <SupportDeskCollapsedPane
              label="Chamados"
              expandLabel="Expandir lista de chamados"
              side="start"
              onExpand={toggleTickets}
            />
          ) : (
            <>
          {isLargeLayout && !ticketsCollapsed ? (
            <ColumnResizeHandle
              onResizeStart={beginQueueColumnResize}
              label="Redimensionar coluna de chamados"
            />
          ) : null}
          <div className="space-y-2 border-b border-secondary-100 p-3">
            {isLargeLayout ? (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                  Chamados
                </span>
                <SupportDeskPaneCollapseButton
                  collapsed={false}
                  side="start"
                  collapseLabel="Recolher lista de chamados"
                  expandLabel="Expandir lista de chamados"
                  onToggle={toggleTickets}
                />
              </div>
            ) : null}
            <div className="flex gap-2 lg:hidden">
              <select
                className="erp-input w-full text-sm"
                value={view}
                onChange={(event) => setView(event.target.value as SupportDeskView)}
              >
                {VIEW_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label} ({countForView(queue?.counts, option.id)})
                  </option>
                ))}
              </select>
            </div>
            <input
              className="erp-input w-full text-sm"
              placeholder="Buscar código, cliente, título..."
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
            />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <select
                className="erp-input w-full text-sm sm:col-span-2"
                value={queueFilter}
                onChange={(event) =>
                  setQueueFilter(event.target.value as SupportDeskQueueFilter)
                }
                aria-label="Filtrar por fila"
              >
                <option value="all">Todas as filas</option>
                {VIEW_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                className="erp-input w-full text-sm"
                value={priority}
                onChange={(event) => patchFilters({ priority: event.target.value || null })}
                aria-label="Filtrar por prioridade"
              >
                <option value="">Prioridade</option>
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                className="erp-input w-full text-sm"
                value={slaState}
                onChange={(event) => patchFilters({ sla: event.target.value || null })}
                aria-label="Filtrar por SLA"
              >
                <option value="">SLA</option>
                {SLA_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                className="erp-input w-full text-sm"
                value={teamId}
                onChange={(event) => patchFilters({ teamId: event.target.value || null })}
                aria-label="Filtrar por equipe"
              >
                <option value="">Equipe</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              <select
                className="erp-input w-full text-sm"
                value={catalogItemId}
                onChange={(event) => patchFilters({ catalogItemId: event.target.value || null })}
                aria-label="Filtrar por categoria"
              >
                <option value="">Categoria</option>
                {catalogItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <select
                className="erp-input w-full text-sm sm:col-span-2"
                value={tag}
                onChange={(event) => patchFilters({ tag: event.target.value || null })}
                aria-label="Filtrar por tag"
              >
                <option value="">Tag</option>
                {[
                  ...(tag && !(queue?.availableTags ?? []).includes(tag) ? [tag] : []),
                  ...(queue?.availableTags ?? []),
                ].map((availableTag) => (
                  <option key={availableTag} value={availableTag}>
                    {availableTag}
                  </option>
                ))}
              </select>
            </div>
            {activeFiltersCount > 0 ? (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-secondary-500">
                  {activeFiltersCount} filtro{activeFiltersCount > 1 ? 's' : ''} ativo
                  {activeFiltersCount > 1 ? 's' : ''}
                </span>
                <button
                  type="button"
                  className="text-xs font-medium text-primary-700 hover:underline"
                  onClick={clearFilters}
                >
                  Limpar filtros
                </button>
              </div>
            ) : null}
            {showGuidance ? (
              <p className="text-xs text-secondary-500">
                {queueFilter === 'all'
                  ? search.trim()
                    ? 'Buscando em todas as filas.'
                    : 'Todas as filas: digite na busca para localizar em qualquer fila.'
                  : activeView.hint}
              </p>
            ) : null}
          </div>

          <div ref={queueScrollRef} className="min-h-0 flex-1 overflow-y-auto">
            {loadingQueue ? (
              <div className="flex justify-center py-10">
                <LoadingSpinner />
              </div>
            ) : !queue || queue.items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-secondary-500">
                {activeFiltersCount > 0
                  ? 'Nenhum chamado com estes filtros.'
                  : 'Nenhum chamado nesta fila.'}
              </div>
            ) : (
              <>
              <ul ref={queueListRef}>
                {queue.items.map((item) => {
                  const active = item.id === selectedId;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        data-desk-ticket-id={item.id}
                        onClick={() => selectTicket(item)}
                        className={`block w-full border-b border-secondary-100 px-3 py-3 text-left transition ${
                          active ? 'bg-primary-50' : 'hover:bg-secondary-50'
                        }`}
                      >
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="font-mono text-xs font-semibold text-primary-700">
                            {item.ticketCode}
                          </span>
                          <span className="text-xs text-secondary-400">
                            {formatRelative(item.updatedAtUtc)}
                          </span>
                        </div>
                        <div className="line-clamp-1 text-sm font-medium text-secondary-900">
                          {item.title}
                        </div>
                        <div className="mt-1 line-clamp-1 text-xs text-secondary-500">
                          {item.companyName}
                          {item.contactName ? ` · ${item.contactName}` : ''}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          <SupportToneBadge tone={ticketStatusTone(item.status)}>
                            {STATUS_LABELS[item.status] ?? item.status}
                          </SupportToneBadge>
                          <SupportToneBadge tone={ticketPriorityTone(item.priority)}>
                            {PRIORITY_LABELS[item.priority] ?? item.priority}
                          </SupportToneBadge>
                          <SupportToneBadge tone={ticketChannelTone()}>
                            {formatChannelOrigin(item.sourceChannel, item.sourceChannelName)}
                          </SupportToneBadge>
                          <SupportToneBadge
                            tone={item.conversationOwner === 'Human' ? 'info' : 'neutral'}
                          >
                            {item.conversationOwnerLabel || 'Assistente'}
                          </SupportToneBadge>
                          {(item.alertOccurrenceCount ?? 0) > 1 ? (
                            <SupportToneBadge tone="caution">
                              {item.alertOccurrenceCount} ocorrências
                            </SupportToneBadge>
                          ) : null}
                          {item.slaState === 'Breached' || item.slaState === 'Warning' ? (
                            <SupportToneBadge tone={ticketSlaTone(item.slaState)}>
                              {item.slaState === 'Breached' ? 'SLA vencido' : 'SLA perto'}
                            </SupportToneBadge>
                          ) : null}
                          {item.isUnassigned ? (
                            <SupportToneBadge tone="neutral">Sem responsável</SupportToneBadge>
                          ) : null}
                          {item.isGrouped ? (
                            <SupportToneBadge tone="info">
                              {item.problemCode ?? 'Agrupado'}
                            </SupportToneBadge>
                          ) : null}
                          {item.waitingForFirstResponse ? (
                            <SupportToneBadge tone="caution">1ª resposta</SupportToneBadge>
                          ) : null}
                          {item.tags
                            ? item.tags
                                .split(',')
                                .map((tag) => tag.trim())
                                .filter(Boolean)
                                .slice(0, 3)
                                .map((tag) => (
                                  <SupportToneBadge key={tag} tone="neutral">
                                    {tag}
                                  </SupportToneBadge>
                                ))
                            : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {loadingMoreQueue ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner />
                </div>
              ) : null}
              {queue.items.length < queue.totalCount ? (
                <p className="px-3 py-2 text-center text-xs text-secondary-500">
                  Exibindo {queue.items.length} de {queue.totalCount} chamados
                  {queue.nextCursor ? ' — role para carregar mais' : ''}
                </p>
              ) : null}
              </>
            )}
          </div>
            </>
          )}
        </section>

        <section className="flex min-h-0 flex-col bg-secondary-50">
          {!selectedId ? (
            <div className="m-auto max-w-md px-6 py-16 text-center">
              <h2 className="text-lg font-semibold text-secondary-900">Selecione um chamado</h2>
              <p className="mt-2 text-sm text-secondary-500">
                A conversa, o contexto do cliente e a próxima ação aparecem aqui. Use as filas à
                esquerda para filtrar por responsabilidade, agrupamento ou arquivo.
              </p>
            </div>
          ) : loadingDetail ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6">
              <LoadingSpinner />
              <button
                type="button"
                className="erp-btn-secondary inline-flex items-center gap-2 text-sm"
                onClick={cancelDetailLoad}
              >
                <XMarkIcon className="h-4 w-4" aria-hidden />
                Cancelar
              </button>
            </div>
          ) : detailError || !detail ? (
            <div className="m-auto max-w-md px-6 py-16 text-center">
              <h2 className="text-lg font-semibold text-secondary-900">
                {detailError ? 'Não foi possível abrir o chamado' : 'Chamado indisponível'}
              </h2>
              <p className="mt-2 text-sm text-secondary-500">
                {detailError ?? 'Selecione outro chamado na fila ou tente novamente.'}
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                {selectedId ? (
                  <button
                    type="button"
                    className="erp-btn-primary inline-flex items-center gap-2 text-sm"
                    onClick={() => void loadDetail(selectedId)}
                  >
                    <ArrowPathIcon className="h-4 w-4" aria-hidden />
                    Tentar novamente
                  </button>
                ) : null}
                <button
                  type="button"
                  className="erp-btn-secondary text-sm"
                  onClick={clearSelection}
                >
                  Fechar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div
                ref={detailHeaderRef}
                className="border-b border-secondary-200 bg-white px-4 py-3"
              >
                <div className="flex items-center gap-1">
                  <span className="font-mono text-xs font-semibold text-primary-700">
                    {detail.ticketCode}
                  </span>
                  <button
                    type="button"
                    title="Copiar código"
                    aria-label="Copiar código do ticket"
                    className="inline-flex items-center justify-center rounded p-0.5 text-secondary-400 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    onClick={() => {
                      void navigator.clipboard.writeText(detail.ticketCode).then(
                        () => toast.success('Código copiado.'),
                        () => toast.error('Não foi possível copiar o código.'),
                      );
                    }}
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
                <h2 className="text-base font-semibold text-secondary-900">{detail.title}</h2>
                <p className="mt-1 text-sm text-secondary-500">
                  {detail.companyName}
                  {detail.contactName ? ` · ${detail.contactName}` : ''}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <SupportToneBadge tone={ticketStatusTone(detail.status)}>
                    {STATUS_LABELS[detail.status] ?? detail.status}
                  </SupportToneBadge>
                  <SupportToneBadge tone={ticketPriorityTone(detail.priority)}>
                    {PRIORITY_LABELS[detail.priority] ?? detail.priority}
                  </SupportToneBadge>
                  <SupportToneBadge tone={ticketChannelTone()}>
                    {formatChannelOrigin(
                      detail.sourceChannel,
                      detail.sourceChannelName ?? selectedQueueItem?.sourceChannelName,
                    )}
                  </SupportToneBadge>
                  <SupportToneBadge
                    tone={
                      (selectedQueueItem?.conversationOwner ?? detail.conversationOwner) ===
                      'Human'
                        ? 'info'
                        : 'neutral'
                    }
                  >
                    {selectedQueueItem?.conversationOwnerLabel
                      ?? detail.conversationOwnerLabel
                      ?? 'Assistente'}
                  </SupportToneBadge>
                  {(selectedQueueItem?.alertOccurrenceCount ?? detail.alertOccurrenceCount ?? 0) >
                  1 ? (
                    <SupportToneBadge tone="caution">
                      {(selectedQueueItem?.alertOccurrenceCount ?? detail.alertOccurrenceCount)}{' '}
                      ocorrências
                    </SupportToneBadge>
                  ) : null}
                  {selectedQueueItem &&
                  (selectedQueueItem.slaState === 'Breached' ||
                    selectedQueueItem.slaState === 'Warning') ? (
                    <SupportToneBadge tone={ticketSlaTone(selectedQueueItem.slaState)}>
                      {selectedQueueItem.slaState === 'Breached' ? 'SLA vencido' : 'SLA perto'}
                    </SupportToneBadge>
                  ) : null}
                  {detail.problemCode ? (
                    <SupportToneBadge tone="info">{detail.problemCode}</SupportToneBadge>
                  ) : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {canWrite && detail.assignedToUserId == null && !selectedQueueItem?.isArchived ? (
                    <SupportDeskActionButton
                      label="Assumir"
                      icon={HandRaisedIcon}
                      variant="primary"
                      showLabel={showActionLabels}
                      tooltip="Assumir atendimento (atalho: A)"
                      onClick={() => void handleClaim()}
                    />
                  ) : null}
                  {canWrite
                  && ((selectedQueueItem?.conversationOwner ?? detail.conversationOwner) === 'Human')
                  && !selectedQueueItem?.isArchived ? (
                    <SupportDeskActionButton
                      label="Devolver ao assistente"
                      icon={ArrowUturnLeftIcon}
                      showLabel={showActionLabels}
                      tooltip="Libera o assistente neste chamado (não remove o responsável)"
                      onClick={() => void handleReturnToAssistant()}
                    />
                  ) : null}
                  {canWrite && detail.problemId && !selectedQueueItem?.isArchived ? (
                    <SupportDeskActionButton
                      label="Responder ao grupo"
                      icon={MegaphoneIcon}
                      showLabel={showActionLabels}
                      onClick={() => openBroadcast()}
                    />
                  ) : null}
                  {canWrite && !selectedQueueItem?.isArchived ? (
                    <SupportDeskActionButton
                      label="Arquivar"
                      icon={ArchiveBoxIcon}
                      showLabel={showActionLabels}
                      onClick={() => openArchivePanel('archive')}
                    />
                  ) : null}
                  {canWrite ? (
                    <>
                      <SupportDeskActionButton
                        label={sendingSurvey === 'Csat' ? 'Enviando…' : 'Enviar CSAT'}
                        icon={FaceSmileIcon}
                        showLabel={showActionLabels}
                        disabled={sendingSurvey !== null}
                        tooltip="Envia CSAT por e-mail ao contato. Não há envio duplicado: se o automático já tiver ocorrido neste chamado, o manual não será enviado."
                        onClick={() => void handleSendSurvey('Csat')}
                      />
                      <SupportDeskActionButton
                        label={sendingSurvey === 'Nps' ? 'Enviando…' : 'Enviar NPS'}
                        icon={SignalIcon}
                        showLabel={showActionLabels}
                        disabled={sendingSurvey !== null}
                        tooltip="Envia NPS por e-mail (requer contrato). Não há envio duplicado: se já existir NPS aberto para a empresa (ex.: automático), o manual não será enviado."
                        onClick={() => void handleSendSurvey('Nps')}
                      />                    </>
                  ) : null}
                  {canWrite && selectedQueueItem?.isArchived ? (
                    <SupportDeskActionButton
                      label="Reabrir"
                      icon={ArrowPathIcon}
                      variant="primary"
                      showLabel={showActionLabels}
                      onClick={() => openArchivePanel('reopen')}
                    />
                  ) : null}
                </div>
                {selectedQueueItem && showGuidance ? (
                  <div className="mt-3 rounded-lg border border-primary-100 bg-primary-50 px-3 py-2 text-sm text-primary-900">
                    <span className="font-medium">Próximo passo: </span>
                    {selectedQueueItem.nextActionLabel}
                  </div>
                ) : null}
              </div>

              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
                {detail.description ? (
                  <article className="rounded-xl border border-secondary-200 bg-white p-4 text-sm text-secondary-800">
                    <SupportMessageContent body={detail.description} />
                  </article>
                ) : null}
                {(detail.comments ?? []).map((comment) => (
                  <article
                    key={comment.id}
                    className={`rounded-xl border p-4 text-sm ${
                      comment.isInternal
                        ? 'border-amber-200 bg-amber-50 text-amber-950'
                        : 'border-secondary-200 bg-white text-secondary-800'
                    }`}
                  >
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-secondary-500">
                      <span>
                        {comment.createdByUserName ?? 'Usuário'}
                        {comment.isInternal ? ' · interna' : ''}
                        {comment.isHtml ? ' · HTML' : ''}
                      </span>
                      <span className="flex items-center gap-2">
                        <span>{new Date(comment.createdAtUtc).toLocaleString('pt-BR')}</span>
                        {canWrite && !comment.isInternal && !selectedQueueItem?.isArchived ? (
                          <button
                            type="button"
                            className="text-primary-700 hover:underline disabled:opacity-50"
                            disabled={resendingCommentId === comment.id || sending}
                            onClick={() => void handleResendComment(comment.id)}
                          >
                            {resendingCommentId === comment.id
                              ? 'Reenviando...'
                              : 'Reenviar pelo canal'}
                          </button>
                        ) : null}
                      </span>
                    </div>
                    <SupportMessageContent body={comment.body} isHtml={Boolean(comment.isHtml)} />
                  </article>
                ))}
              </div>

              {archivePanel ? (
                <div className="max-h-[55%] min-h-[14rem] shrink-0">
                  <SupportArchivePanel
                    mode={archivePanel}
                    ticketId={detail.id}
                    ticketCode={detail.ticketCode}
                    submitting={archiveSubmitting}
                    onClose={() => setArchivePanel(null)}
                    onSubmit={archivePanel === 'archive' ? handleArchiveSubmit : handleReopenSubmit}
                  />
                </div>
              ) : broadcastOpen && detail.problemId ? (
                <div className="max-h-[55%] min-h-[16rem] shrink-0">
                  <SupportBroadcastComposer
                    problemId={detail.problemId}
                    problemCode={detail.problemCode}
                    workaround={linkedProblem?.workaround}
                    contextTicketId={detail.id}
                    onClose={() => setBroadcastOpen(false)}
                    onCompleted={() => {
                      if (selectedId) void loadDetail(selectedId, { silent: true });
                      void loadQueue({ silent: true });
                    }}
                  />
                </div>
              ) : canWrite && !selectedQueueItem?.isArchived ? (
                <form
                  onSubmit={(event) => void handleReply(event)}
                  className="border-t border-secondary-200 bg-white p-4"
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <label className="text-xs font-medium text-secondary-700" htmlFor="desk-reply-body">
                      {replyInternal ? 'Nota interna' : 'Resposta ao cliente'}
                    </label>
                    <div className="flex items-center gap-2">
                      {!replyInternal ? (
                        <label className="inline-flex items-center gap-1.5 text-xs text-secondary-600">
                          <input
                            type="checkbox"
                            checked={replyHtmlMode}
                            disabled={sending || suggestingReply}
                            onChange={(event) => setReplyHtmlMode(event.target.checked)}
                          />
                          Modo HTML
                        </label>
                      ) : null}
                      {cannedResponses.length > 0 ? (
                        <select
                          className="erp-input max-w-[12rem] py-1 text-xs"
                          defaultValue=""
                          disabled={sending || suggestingReply}
                          aria-label="Inserir resposta pronta"
                          onChange={(event) => {
                            const selected = cannedResponses.find((item) => item.id === event.target.value);
                            if (selected) {
                              setReplyBody((current) =>
                                current.trim()
                                  ? `${current.trim()}\n\n${selected.body}`
                                  : selected.body,
                              );
                            }
                            event.target.value = '';
                          }}
                        >
                          <option value="">Resposta pronta…</option>
                          {cannedResponses.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.title}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      <SupportAiSuggestButton
                        suggesting={suggestingReply}
                        disabled={sending}
                        onClick={() => void handleSuggestReply()}
                        title={
                          replyInternal
                            ? 'Sugerir nota interna com IA'
                            : 'Sugerir resposta ao cliente com IA'
                        }
                      />
                    </div>
                  </div>
                  {replyHtmlMode && !replyInternal ? (
                    <div id="desk-reply-body">
                      <RichTextEditor
                        value={replyBody}
                        onChange={setReplyBody}
                        readOnly={sending}
                        minHeight="140px"
                        placeholder="Escreva a resposta em HTML (negrito, links, listas)…"
                      />
                    </div>
                  ) : (
                    <textarea
                      ref={replyTextareaRef}
                      id="desk-reply-body"
                      className="erp-input w-full text-sm"
                      rows={3}
                      placeholder={
                        replyInternal
                          ? 'Nota interna para a equipe...'
                          : 'Escreva a resposta ao cliente... (atalho: Enter)'
                      }
                      value={replyBody}
                      onChange={(event) => setReplyBody(event.target.value)}
                      disabled={sending}
                    />
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <HoverTooltip text="Anexar arquivos">
                      <label className={`inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-xl border border-secondary-300 bg-secondary-100 text-secondary-700 transition-colors hover:bg-secondary-200 ${sending ? 'cursor-not-allowed opacity-50' : ''}`}>
                        <PaperClipIcon className="h-5 w-5" aria-hidden="true" />
                        <span className="sr-only">Anexar arquivos</span>
                        <input
                          type="file"
                          className="sr-only"
                          multiple
                          disabled={sending}
                          onChange={(event) => {
                            const selected = Array.from(event.target.files ?? []);
                            if (selected.length === 0) return;
                            setReplyFiles((current) => [...current, ...selected]);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    </HoverTooltip>
                    {replyFiles.length > 0 ? (
                      <ul className="flex flex-wrap gap-2 text-xs text-secondary-600">
                        {replyFiles.map((file, index) => (
                          <li
                            key={`${file.name}-${file.size}-${index}`}
                            className="inline-flex items-center gap-1 rounded-md border border-secondary-200 bg-secondary-50 px-2 py-1"
                          >
                            <span className="max-w-[12rem] truncate">{file.name}</span>
                            <button
                              type="button"
                              className="text-secondary-500 hover:text-danger-600"
                              disabled={sending}
                              onClick={() =>
                                setReplyFiles((current) => current.filter((_, i) => i !== index))
                              }
                              aria-label={`Remover ${file.name}`}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <HoverTooltip
                      className="ml-auto"
                      text="Nota visível só para a equipe. Não é enviada ao cliente."
                    >
                      <label className="inline-flex items-center gap-2 text-sm text-secondary-700">
                        <input
                          type="checkbox"
                          checked={replyInternal}
                          disabled={sending}
                          onChange={(event) => {
                            setReplyInternal(event.target.checked);
                            if (event.target.checked) {
                              setReplyHtmlMode(false);
                            }
                          }}
                        />
                        Nota interna
                      </label>
                    </HoverTooltip>
                    <HoverTooltip text={replyInternal ? 'Registrar nota interna' : 'Enviar resposta ao cliente'}>
                      <button
                        type="submit"
                        className="erp-btn-primary gap-1.5 px-3 py-2 text-sm"
                        disabled={
                          sending
                          || (
                            replyHtmlMode && !replyInternal
                              ? isBlankHtml(replyBody) && replyFiles.length === 0
                              : !replyBody.trim() && replyFiles.length === 0
                          )
                        }
                      >
                        <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />
                        {sending ? 'Enviando...' : 'Enviar'}
                      </button>
                    </HoverTooltip>
                  </div>
                </form>
              ) : null}
            </>
          )}
        </section>

        <aside className="hidden min-h-0 flex-col overflow-y-auto border-l border-secondary-200 bg-white xl:flex">
          {classificationCollapsed ? (
            <SupportDeskCollapsedPane
              label="Classificação"
              expandLabel="Expandir classificação"
              side="end"
              onExpand={toggleClassification}
            />
          ) : (
            <>
          <div className="flex items-center justify-between gap-2 border-b border-secondary-100 px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
              Classificação
            </span>
            <SupportDeskPaneCollapseButton
              collapsed={false}
              side="end"
              collapseLabel="Recolher classificação"
              expandLabel="Expandir classificação"
              onToggle={toggleClassification}
            />
          </div>
          {detail ? (
            <dl className="space-y-4 p-4 text-sm">
              <div>
                <dt className="mb-1 text-xs text-secondary-500">Status</dt>
                <dd>
                  {canWrite ? (
                    <InlineOptionPicker
                      valueId={detail.status}
                      valueLabel={STATUS_LABELS[detail.status] ?? detail.status}
                      options={statusOptions}
                      disabled={classifying}
                      saving={classifying}
                      searchable={false}
                      renderTrigger={(label) => (
                        <SupportToneBadge tone={ticketStatusTone(detail.status)}>{label}</SupportToneBadge>
                      )}
                      onCommit={(option) => {
                        if (!option || option.id === detail.status) return;
                        void handleClassify({ status: option.id });
                      }}
                    />
                  ) : (
                    <SupportToneBadge tone={ticketStatusTone(detail.status)}>
                      {STATUS_LABELS[detail.status] ?? detail.status}
                    </SupportToneBadge>
                  )}
                </dd>
              </div>

              <div>
                <dt className="mb-1 text-xs text-secondary-500">Prioridade</dt>
                <dd>
                  {canWrite ? (
                    <InlineOptionPicker
                      valueId={detail.priority}
                      valueLabel={PRIORITY_LABELS[detail.priority] ?? detail.priority}
                      options={priorityOptions}
                      disabled={classifying}
                      saving={classifying}
                      searchable={false}
                      renderTrigger={(label) => (
                        <SupportToneBadge tone={ticketPriorityTone(detail.priority)}>{label}</SupportToneBadge>
                      )}
                      onCommit={(option) => {
                        if (!option || option.id === detail.priority) return;
                        void handleClassify({ priority: option.id });
                      }}
                    />
                  ) : (
                    <SupportToneBadge tone={ticketPriorityTone(detail.priority)}>
                      {PRIORITY_LABELS[detail.priority] ?? detail.priority}
                    </SupportToneBadge>
                  )}
                </dd>
              </div>

              <div>
                <dt className="mb-1 text-xs text-secondary-500">Categoria</dt>
                <dd>
                  {canWrite ? (
                    <InlineOptionPicker
                      valueId={detail.serviceCatalogItemId}
                      valueLabel={detail.serviceCatalogItemName ?? 'Sem categoria'}
                      options={catalogOptions}
                      emptyLabel="Sem categoria"
                      allowClear
                      clearLabel="Remover categoria"
                      disabled={classifying}
                      saving={classifying}
                      onCommit={(option) => {
                        if (!option) {
                          void handleClassify({ clearCatalogItem: true });
                          return;
                        }
                        if (option.id === detail.serviceCatalogItemId) return;
                        void handleClassify({ serviceCatalogItemId: option.id });
                      }}
                    />
                  ) : (
                    <span className="font-medium text-secondary-900">
                      {detail.serviceCatalogItemName ?? '—'}
                    </span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="mb-1 text-xs text-secondary-500">Tags</dt>
                <dd>
                  <SupportTagsPicker
                    value={detail.tags}
                    disabled={!canWrite}
                    saving={classifying}
                    onCommit={(tags) =>
                      void handleClassify(tags ? { tags } : { clearTags: true })
                    }
                  />
                </dd>
              </div>

              <div>
                <dt className="mb-1 text-xs text-secondary-500">Dono da conversa</dt>
                <dd className="mb-3 text-sm text-secondary-800">
                  {selectedQueueItem?.conversationOwnerLabel
                    ?? detail.conversationOwnerLabel
                    ?? 'Assistente'}
                </dd>
                <dt className="mb-1 text-xs text-secondary-500">Responsável</dt>
                <dd>
                  {canWrite ? (
                    <CollaboratorAutocomplete
                      valueUserId={detail.assignedToUserId}
                      valueUserName={detail.assignedToUserName}
                      optionsSource={assigneeOptions}
                      allowClear
                      disabled={classifying}
                      saving={classifying}
                      onCommit={(user) => {
                        if (!user) {
                          void handleClassify({ clearAssignee: true });
                          return;
                        }
                        if (user.userId === detail.assignedToUserId) return;
                        void handleClassify({ assignedToUserId: user.userId });
                      }}
                    />
                  ) : (
                    <span className="font-medium text-secondary-900">
                      {detail.assignedToUserName ?? '—'}
                    </span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="mb-1 text-xs text-secondary-500">Equipe</dt>
                <dd>
                  {canWrite ? (
                    <InlineOptionPicker
                      valueId={detail.assignedTeamId}
                      valueLabel={detail.assignedTeamName ?? 'Sem equipe'}
                      options={teamOptions}
                      emptyLabel="Sem equipe"
                      allowClear
                      clearLabel="Remover equipe"
                      disabled={classifying}
                      saving={classifying}
                      onCommit={(option) => {
                        if (!option) {
                          void handleClassify({ clearTeam: true });
                          return;
                        }
                        if (option.id === detail.assignedTeamId) return;
                        void handleClassify({ assignedTeamId: option.id });
                      }}
                    />
                  ) : (
                    <span className="font-medium text-secondary-900">
                      {detail.assignedTeamName ?? '—'}
                    </span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="mb-1 text-xs text-secondary-500">Problema (agrupamento)</dt>
                <dd>
                  {canWrite ? (
                    <InlineOptionPicker
                      valueId={detail.problemId}
                      valueLabel={detail.problemCode ?? 'Sem agrupamento'}
                      options={problemOptions}
                      emptyLabel="Sem agrupamento"
                      allowClear
                      clearLabel="Desvincular problema"
                      disabled={classifying}
                      saving={classifying}
                      onCommit={(option) => {
                        if (!option) {
                          void handleClassify({ clearProblem: true });
                          return;
                        }
                        if (option.id === detail.problemId) return;
                        void handleClassify({ problemId: option.id });
                      }}
                    />
                  ) : detail.problemCode ? (
                    <Link to="/customer-success/problems" className="text-primary-700 hover:underline">
                      {detail.problemCode}
                    </Link>
                  ) : (
                    <span>—</span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="text-xs text-secondary-500">Canal</dt>
                <dd className="mt-1">
                  <SupportToneBadge tone={ticketChannelTone()}>
                    {formatChannelOrigin(
                      detail.sourceChannel,
                      detail.sourceChannelName ?? selectedQueueItem?.sourceChannelName,
                    )}
                  </SupportToneBadge>
                </dd>
              </div>

              <div>
                <dt className="text-xs text-secondary-500">SLA resposta</dt>
                <dd className="font-medium text-secondary-900">
                  {detail.slaResponseDueAtUtc
                    ? new Date(detail.slaResponseDueAtUtc).toLocaleString('pt-BR')
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-secondary-500">SLA resolução</dt>
                <dd className="font-medium text-secondary-900">
                  {detail.slaResolutionDueAtUtc
                    ? new Date(detail.slaResolutionDueAtUtc).toLocaleString('pt-BR')
                    : '—'}
                </dd>
              </div>

              {showGuidance ? (
                <p className="rounded-lg bg-secondary-50 p-3 text-xs text-secondary-600">
                  Clique nos chips para classificar. Categoria vem do catálogo de serviços; tags
                  são livres (ex.: vip, acesso). Agrupe no mesmo problema para responder em massa.
                  Atalhos: J/K navegam a fila, Enter foca a resposta, A assume, Esc fecha painéis.
                </p>
              ) : null}
            </dl>
          ) : (
            <p className="p-4 text-sm text-secondary-500">
              Selecione um chamado para classificar status, tags, categoria e responsável.
            </p>
          )}
            </>
          )}
        </aside>
      </div>

      <SupportAiSuggestModal
        open={aiSuggestOpen}
        loading={suggestingReply}
        error={aiSuggestError}
        text={aiSuggestText}
        isInternal={replyInternal}
        onTextChange={setAiSuggestText}
        onClose={closeAiSuggest}
        onUse={useAiSuggestion}
        onRegenerate={() => void handleSuggestReply()}
      />
    </div>
  );
}
